import { Box, IconButton, ListSubheader, MenuItem, Paper, Select, SelectChangeEvent, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import React from 'react';
import { useDashboardFilters, DashboardFilterType } from '../../../hooks/useDashboardFilters';
import { useDashboardPagination } from '../../../hooks/useDashboardPagination';
import { useDateRange } from '../../../hooks/useGetDateRange';
import { useUserDashboardSettings } from '../../../hooks/useUserDashboardSettings';
import { useGetAllEvents, useGetEventsAttendance } from '../../../services/react-query/events';
import { useGetCurrentUserSettings } from '../../../services/react-query/users/useGetCurrentUserSettings';
import { EventTimeSpan } from '../../../types/Event';
import { Filter } from '../../../types/Filter';
import { UserConfigurableModule } from '../../../types/User';
import { Loading } from '../../../uikit';
import { controlSectionStyles, dashboardSelectSx, iconButtonStyles, paperContainerStyles, tilesContainerStyles } from '../dashboardStyles';
import { EventFooter } from './EventFooter';
import { EventTile } from './EventTile';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import _ from 'lodash';

interface IEventsPanelProps {
  currentEmployeeUuid?: string;
}

export function EventsPanel({ currentEmployeeUuid }: IEventsPanelProps) {
  const { dashboardSettings, updateSettings } = useUserDashboardSettings(UserConfigurableModule.EVENTS);

  const { data: currentUserSettings, isLoading: isUserSettingsLoading } = useGetCurrentUserSettings();

  // Apply employee filter when "My Events" mode is enabled
  const isMy = dashboardSettings.ownership === 'my';
  const employees = currentEmployeeUuid && isMy ? [currentEmployeeUuid] : [];

  // Build built-in default filters with dynamic date ranges
  const rangeToday = useDateRange(EventTimeSpan.TODAY);
  const rangeTomorrow = useDateRange(EventTimeSpan.TOMORROW);
  const rangeWeek = useDateRange(EventTimeSpan.WEEK);
  const rangeUpcoming = useDateRange(EventTimeSpan.UPCOMING);

  const defaultFilters: Filter[] = [
    {
      uuid: EventTimeSpan.TODAY,
      name: 'Today',
      page: UserConfigurableModule.EVENTS,
      content: { employees, date_range: rangeToday },
    },
    {
      uuid: EventTimeSpan.TOMORROW,
      name: 'Tomorrow',
      page: UserConfigurableModule.EVENTS,
      content: { employees, date_range: rangeTomorrow },
    },
    {
      uuid: EventTimeSpan.WEEK,
      name: 'This Week',
      page: UserConfigurableModule.EVENTS,
      content: { employees, date_range: rangeWeek },
    },
    {
      uuid: EventTimeSpan.UPCOMING,
      name: 'Upcoming',
      page: UserConfigurableModule.EVENTS,
      content: { employees, date_range: rangeUpcoming },
    },
  ];

  // Centralized filter + span restore logic
  const { savedFilters, selectedFilter, setSelectedFilter, filterType, filtersParam, isUserFiltersLoading } = useDashboardFilters({
    module: UserConfigurableModule.EVENTS,
    defaultFilters,
    savedSpan: dashboardSettings.filters?.span,
  });

  // Extracted pagination logic (page bounds handled internally)
  const { page, setNewPage } = useDashboardPagination();

  // Fetch events using current filter + pagination
  const { data, isLoading: isEventsLoading } = useGetAllEvents({
    enabled: !!selectedFilter,
    params: {
      page,
      sort: { start_at: 1 },
      per_page: 6,
      filters: filtersParam,
    },
  });

  const { data: attendanceData } = useGetEventsAttendance({
    uuids: _.map(data?.data, 'uuid'),
    employeeUuid: currentEmployeeUuid ?? '',
    enabled: filterType == DashboardFilterType.DEFAULT && isMy
  });

  if (isEventsLoading || isUserFiltersLoading || isUserSettingsLoading || !selectedFilter) {
    return <Loading />;
  }

  // Total pages returned from API
  const maxPage = data?.meta?.total_pages || 1;

  // When user selects a new filter from dropdown
  const handleFilterChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;
    setSelectedFilter(value); // update selected item in UI

    // Persist selection to user dashboard settings (for reload/navigation restore)
    updateSettings({ filters: { span: value } });
  };

  return (
    <Box component={Paper} sx={paperContainerStyles}>
      <Box sx={controlSectionStyles}>
        {/* Only admins can toggle My Events / All Events */}
        {currentUserSettings?.settings.isAdmin ? (
          <Select
            size="small"
            sx={dashboardSelectSx}
            value={dashboardSettings.ownership}
            onChange={(el) => updateSettings({ ownership: el.target.value })}
            disabled={filterType === DashboardFilterType.SAVED}
          >
            <MenuItem value="my" disabled={!currentEmployeeUuid}>
              My Events
            </MenuItem>
            <MenuItem value="all">
              All Events
            </MenuItem>
          </Select>
        ) : (
          <Typography variant="h6" sx={{ height: '3rem', fontWeight: 600, pt: 1 }}>
            Events
          </Typography>
        )}

        {/* Filter selection dropdown */}
        <Select size="small" sx={dashboardSelectSx} value={selectedFilter || ''} onChange={handleFilterChange}>
          <ListSubheader>Default</ListSubheader>
          {defaultFilters.map((f) => (
            <MenuItem key={f.uuid} value={f.uuid}>
              {f.name}
            </MenuItem>
          ))}

          {savedFilters.length > 0 && [
            <ListSubheader key="saved-header">Saved Filters</ListSubheader>,
            ...savedFilters.map((f) => (
              <MenuItem key={f.uuid} value={f.uuid}>
                {f.name}
              </MenuItem>
            )),
          ]}
        </Select>

        {/* Page prev/next controls */}
        <Box sx={{ whiteSpace: 'nowrap' }}>
          <IconButton sx={iconButtonStyles} onClick={() => setNewPage(page - 1, maxPage)} disabled={page <= 1}>
            <KeyboardArrowLeft fontSize="small" />
          </IconButton>

          <IconButton sx={iconButtonStyles} onClick={() => setNewPage(page + 1, maxPage)} disabled={page >= maxPage}>
            <KeyboardArrowRight fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Event cards */}
      <Box sx={tilesContainerStyles}>
        {data?.data?.map((event) => (
          <EventTile
            key={event.uuid}
            event={event}
            attendance={attendanceData?.[event.uuid]}
          />
        ))}
      </Box>

      {/* Footer summary */}
      <EventFooter filters={filtersParam} />
    </Box>
  );
}
